
  import { defineComponent, ref, onMounted } from 'vue';
  import { useRoute, useRouter } from 'vue-router';
  import { Form } from 'vee-validate';
  import { setCurrentPageBreadcrumbs } from '@/core/helpers/breadcrumb';
  import { toHtmlDocument } from '@/core/helpers/htmlDocument';
  import { useI18n } from 'vue-i18n/index';
  import { useStore } from 'vuex';
  import { Actions } from '@/store/enums/StoreEnums';
  import { useAbility } from '@casl/vue';
  import Swal from 'sweetalert2/dist/sweetalert2.js';
  import Editor from '@tinymce/tinymce-vue';
  import { MethodologyObject } from '@/store/modules/Indices/MethodologyModule';

  export default defineComponent({
    name: 'methodology-content-web-editing',
    components: {
      editor: Editor,
      Form,
    },

    async setup() {
      const { t, te } = useI18n();
      const store = useStore();
      const router = useRouter();
      const route = useRoute();
      const { can } = useAbility();
      const submitButton = ref<HTMLButtonElement | null>(null);
      const translate = (text) => {
        if (te(text)) {
          return t(text);
        } else {
          return text;
        }
      };

      onMounted(() => {
        setCurrentPageBreadcrumbs(translate('methodologyContentUpdating'), [
          translate('indices'),
        ]);
      });

      const methodology = ref<MethodologyObject>({} as MethodologyObject);
      const htmlEn = ref('');
      const htmlAr = ref('');

      // A "detailed" page (the full Price/Volume methodology document) is
      // identified by its string `key`; an individual section is identified
      // by its numeric `id`. Both share this same content editor.
      const routeKey = route.params.key as string | undefined;
      const isDetailed = !!routeKey;

      const fetchMethodology = async () => {
        try {
          const { data } = await store.dispatch(
            isDetailed
              ? Actions.GET_METHODOLOGY_DETAILED
              : Actions.GET_METHODOLOGY,
            isDetailed ? routeKey : route.params.id
          );
          methodology.value = data.data;
          htmlAr.value = methodology.value.htmlArWeb;
          htmlEn.value = methodology.value.htmlEnWeb;
        } catch (error) {
          Swal.fire({
            text: translate('FAILED_TO_FETCH_METHODOLOGY'),
            icon: 'error',
          }).then(() => {
            router.push({ name: 'methodology-listing' });
          });
        }
      };

      await fetchMethodology();

      const goBack = () => {
        router.back();
      };

      const onSubmitUpdate = async () => {
        if (submitButton.value) {
          // eslint-disable-next-line
          submitButton.value!.disabled = true;
          submitButton.value.setAttribute('data-kt-indicator', 'on');
        }

        await EditorChangeAr(htmlAr.value);
        await EditorChangeEn(htmlEn.value);

        const identifier = isDetailed
          ? { key: methodology.value.key }
          : { id: methodology.value.id };

        await store.dispatch(
          isDetailed
            ? Actions.UPDATE_METHODOLOGY_DETAILED
            : Actions.UPDATE_METHODOLOGY,
          {
            ...identifier,
            data: {
              htmlArWeb: htmlAr.value,
              htmlEnWeb: htmlEn.value,
            },
          }
        );
        const [errorName] = Object.keys(store.getters.getErrors);
        const error = store.getters.getErrors[errorName];

        if (error) {
          Swal.fire({
            text: translate(error[0]),
            icon: 'error',
            buttonsStyling: false,
            confirmButtonText: translate('tryAgainExcl'),
            customClass: {
              confirmButton: 'btn fw-bold btn-light-danger',
            },
          });
          //Deactivate indicator
          submitButton.value?.removeAttribute('data-kt-indicator');
          // eslint-disable-next-line
          submitButton.value!.disabled = false;
        } else {
          Swal.fire({
            text: translate('SUCCESSFULLY_UPDATED_METHODOLOGY_CONTENT'),
            icon: 'success',
            buttonsStyling: false,
            confirmButtonText: translate('ok'),
            customClass: {
              confirmButton: 'btn btn-light-primary',
            },
          }).then(() => {
            router.push({ name: 'methodology-listing' });
          });
        }
      };

      store.dispatch(Actions.REMOVE_BODY_CLASSNAME, 'page-loading-over');

      const EditorChangeAr = async (newContent) => {
        htmlAr.value = toHtmlDocument(newContent, 'rtl');
      };

      const EditorChangeEn = async (newContent) => {
        htmlEn.value = toHtmlDocument(newContent, 'ltr');
      };

      return {
        translate,
        goBack,
        submitButton,
        onSubmitUpdate,
        htmlEn,
        htmlAr,
        can,
        methodology,
      };
    },
  });
